import type { AnalyticsBreakdownItem, PageTraffic, TrafficPoint } from "../../hooks/useAnalytics";

/**
 * Pure helpers behind the "Pages les plus parcourues" and "Sources" modules of /analytics : merging the
 * page rankings of several instances, grouping pages by section (first path segment), computing the
 * trend against the previous window, and classifying referrer hosts. No React here, so every rule is
 * unit-testable on its own.
 */

// ------------------------------------------------------------------ merging across instances

/** Sum the page rankings of several instances by path (busiest first); sparklines are merged by date. */
export function mergePages(lists: Array<PageTraffic[] | undefined>): PageTraffic[] {
	const byPath = new Map<string, PageTraffic>();
	for (const list of lists) {
		for (const p of list || []) {
			const prev = byPath.get(p.path);
			if (!prev) {
				byPath.set(p.path, {
					path: p.path,
					pageViews: p.pageViews,
					visits: p.visits,
					previousPageViews: p.previousPageViews || 0,
					previousVisits: p.previousVisits || 0,
					series: (p.series || []).map((s) => ({ ...s })),
				});
				continue;
			}
			prev.pageViews += p.pageViews;
			prev.visits += p.visits;
			prev.previousPageViews += p.previousPageViews || 0;
			prev.previousVisits += p.previousVisits || 0;
			prev.series = mergeSeries(prev.series, p.series || []);
		}
	}
	return [...byPath.values()].sort((a, b) => b.pageViews - a.pageViews || a.path.localeCompare(b.path));
}

/** Sum two daily series by date, chronological. */
export function mergeSeries(a: TrafficPoint[], b: TrafficPoint[]): TrafficPoint[] {
	const byDate = new Map<string, TrafficPoint>();
	for (const p of [...a, ...b]) {
		const prev = byDate.get(p.date);
		if (prev) {
			prev.pageViews += p.pageViews;
			prev.visits += p.visits;
		} else {
			byDate.set(p.date, { date: p.date, pageViews: p.pageViews, visits: p.visits });
		}
	}
	return [...byDate.values()].sort((x, y) => x.date.localeCompare(y.date));
}

// ------------------------------------------------------------------ sections

/** Human label of a path's first segment ("/marche-des-changes/x" → "Marché des changes"). */
const SECTION_LABELS: Record<string, string> = {
	"": "Accueil",
	search: "Recherche",
	actualites: "Actualités",
	articles: "Articles",
	"la-banque": "La Banque",
	"marche-des-changes": "Marché des changes",
	statistiques: "Statistiques",
	publications: "Publications",
	reglementation: "Réglementation",
	"stabilite-financiere": "Stabilité financière",
	"politique-monetaire": "Politique monétaire",
	"systeme-financier": "Système financier",
	"institutions-financieres": "Institutions financières",
	"banque-centrale-institution": "Institution (page héritée)",
	contact: "Contact",
	en: "English",
};

/** First path segment, "" for the homepage. */
export function sectionKeyOf(path: string): string {
	const clean = (path || "/").split(/[?#]/)[0];
	const seg = clean.split("/").filter(Boolean)[0] || "";
	return seg.toLowerCase();
}

/** Readable section label: known keys are translated, others humanised ("hotel-des-monnaies" → "Hotel des monnaies"). */
export function sectionLabelOf(path: string): string {
	const key = sectionKeyOf(path);
	if (key in SECTION_LABELS) return SECTION_LABELS[key];
	const words = key.replace(/[-_]+/g, " ").trim();
	return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Accueil";
}

/** A short readable title for a page: last segment humanised; the homepage is "Accueil". */
export function pageTitleOf(path: string): string {
	const segs = (path || "/").split(/[?#]/)[0].split("/").filter(Boolean);
	if (segs.length === 0) return "Accueil";
	const last = decodeURIComponentSafe(segs[segs.length - 1]);
	// Numeric suffix ids ("...-11977") are noise for humans.
	const words = last.replace(/-\d{3,}$/, "").replace(/[-_]+/g, " ").trim();
	return words ? words.charAt(0).toUpperCase() + words.slice(1) : last;
}

function decodeURIComponentSafe(s: string): string {
	try {
		return decodeURIComponent(s);
	} catch {
		return s;
	}
}

/**
 * Stable colour per section: a hash of the KEY (not the rank), so a section keeps its colour whatever
 * the window or the filter shows. Ten distinguishable hues, adjacent hues far apart on the wheel.
 */
export const SECTION_PALETTE = ["#4F46E5", "#0EA5E9", "#16A34A", "#F59E0B", "#DB2777", "#7C3AED", "#0891B2", "#65A30D", "#EA580C", "#475569"];

export function sectionColorOf(path: string): string {
	const key = sectionKeyOf(path);
	let h = 0;
	for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
	return SECTION_PALETTE[h % SECTION_PALETTE.length];
}

export type SectionAggregate = {
	key: string;
	label: string;
	color: string;
	pageViews: number;
	visits: number;
	previousPageViews: number;
	pageCount: number;
	/** Share of the total page views of the ranking, 0..1. */
	share: number;
	/** The busiest page of the section (its path). */
	topPath: string;
};

/** Group a page ranking by section, busiest first, with the share of the ranking's total. */
export function aggregateSections(pages: PageTraffic[]): SectionAggregate[] {
	const total = pages.reduce((s, p) => s + p.pageViews, 0);
	const map = new Map<string, SectionAggregate>();
	const topViews = new Map<string, number>();
	for (const p of pages) {
		const key = sectionKeyOf(p.path);
		let agg = map.get(key);
		if (!agg) {
			agg = {
				key,
				label: sectionLabelOf(p.path),
				color: sectionColorOf(p.path),
				pageViews: 0,
				visits: 0,
				previousPageViews: 0,
				pageCount: 0,
				share: 0,
				topPath: p.path,
			};
			map.set(key, agg);
		}
		agg.pageViews += p.pageViews;
		agg.visits += p.visits;
		agg.previousPageViews += p.previousPageViews || 0;
		agg.pageCount += 1;
		const top = topViews.get(key) ?? -1;
		if (p.pageViews > top) {
			topViews.set(key, p.pageViews);
			agg.topPath = p.path;
		}
	}
	const list = [...map.values()].sort((a, b) => b.pageViews - a.pageViews || a.label.localeCompare(b.label));
	for (const agg of list) agg.share = total > 0 ? agg.pageViews / total : 0;
	return list;
}

// ------------------------------------------------------------------ trend vs previous window

export type Trend = { kind: "new" | "up" | "down" | "flat"; pct: number };

/**
 * Trend of a counter against the previous window: "new" when there was nothing before, "flat" within
 * ±2 %, else up/down with the rounded percentage. Never divides by zero.
 */
export function trendOf(current: number, previous: number): Trend {
	if (!previous || previous <= 0) return { kind: current > 0 ? "new" : "flat", pct: 0 };
	const pct = Math.round(((current - previous) / previous) * 100);
	if (Math.abs(pct) < 2) return { kind: "flat", pct };
	return { kind: pct > 0 ? "up" : "down", pct };
}

/** Above this, a percentage stops being readable ("+9648 %") and the chip shows a multiplier ("×97"). */
export const TREND_MULTIPLIER_FROM = 500;

/** Compact reading of the trend for the chip: "+42 %", "-8 %", "×97", "0 %". */
export function trendText(t: Trend): string {
	if (t.kind === "flat") return `${t.pct > 0 ? "+" : ""}${t.pct} %`;
	if (t.kind === "new") return "Nouveau";
	if (Math.abs(t.pct) >= TREND_MULTIPLIER_FROM) {
		const factor = 1 + t.pct / 100;
		return `×${factor >= 10 ? Math.round(factor) : factor.toFixed(1)}`;
	}
	return `${t.pct > 0 ? "+" : ""}${t.pct} %`;
}

/** Human reading of the trend, for tooltips and screen readers. */
export function trendLabel(t: Trend): string {
	if (t.kind === "new") return "Nouveau sur la période (aucune vue sur la période précédente)";
	if (t.kind === "flat") return "Stable par rapport à la période précédente";
	return `${t.pct > 0 ? "+" : ""}${t.pct} % par rapport à la période précédente de même durée`;
}

// ------------------------------------------------------------------ search filter

/** Case- and accent-insensitive "contains" on the path and its human title. */
export function matchesQuery(page: PageTraffic, query: string): boolean {
	const q = fold(query.trim());
	if (!q) return true;
	return fold(page.path).includes(q) || fold(pageTitleOf(page.path)).includes(q) || fold(sectionLabelOf(page.path)).includes(q);
}

function fold(s: string): string {
	return (s || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "");
}

// ------------------------------------------------------------------ referrers / sources

export type SourceKind = "direct" | "search" | "social" | "other";

const SEARCH_HOSTS = /(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|yahoo\.[a-z.]+|ecosia\.org|qwant\.com|baidu\.com|yandex\.[a-z]+)$/i;
const SEARCH_APPS = /googlequicksearchbox|com\.google\.android|com\.microsoft\.bing/i;
const SOCIAL_HOSTS = /(^|\.)(facebook\.com|fb\.com|m\.facebook\.com|l\.facebook\.com|instagram\.com|twitter\.com|x\.com|t\.co|linkedin\.com|lnkd\.in|youtube\.com|youtu\.be|whatsapp\.com|wa\.me|tiktok\.com|threads\.net|telegram\.org|t\.me|reddit\.com|pinterest\.com)$/i;
const SOCIAL_APPS = /com\.linkedin\.android|com\.facebook|com\.twitter|com\.instagram|org\.telegram|com\.whatsapp|com\.zhiliaoapp\.musically/i;

/** Which family a referrer host belongs to (Cloudflare reports "" for direct / unknown). */
export function classifyReferrer(host: string): SourceKind {
	const h = (host || "").trim().toLowerCase();
	if (!h) return "direct";
	if (SEARCH_HOSTS.test(h) || SEARCH_APPS.test(h)) return "search";
	if (SOCIAL_HOSTS.test(h) || SOCIAL_APPS.test(h)) return "social";
	return "other";
}

export const SOURCE_META: Record<SourceKind, { label: string; color: string; hint: string }> = {
	direct: { label: "Accès direct", color: "#475569", hint: "URL saisie, favori, application ou lien sans référent" },
	search: { label: "Moteurs de recherche", color: "#16A34A", hint: "Google, Bing et autres moteurs" },
	social: { label: "Réseaux sociaux", color: "#0EA5E9", hint: "Facebook, X, LinkedIn, WhatsApp, YouTube…" },
	other: { label: "Autres sites", color: "#F59E0B", hint: "Liens depuis d'autres sites web" },
};

export type SourceAggregate = {
	kind: SourceKind;
	label: string;
	color: string;
	pageViews: number;
	visits: number;
	share: number;
	/** The busiest hosts of the family (empty for direct). */
	hosts: AnalyticsBreakdownItem[];
};

/** Merge referrer lists across instances (by host, case-insensitive), busiest first. */
export function mergeReferrers(lists: Array<AnalyticsBreakdownItem[] | undefined>): AnalyticsBreakdownItem[] {
	const byHost = new Map<string, AnalyticsBreakdownItem>();
	for (const list of lists) {
		for (const r of list || []) {
			const key = (r.label || "").toLowerCase();
			const prev = byHost.get(key);
			if (prev) {
				prev.pageViews += r.pageViews;
				prev.visits += r.visits;
			} else {
				byHost.set(key, { label: key, pageViews: r.pageViews, visits: r.visits });
			}
		}
	}
	return [...byHost.values()].sort((a, b) => b.pageViews - a.pageViews);
}

/** Group referrers into the four source families, always in the fixed order direct/search/social/other. */
export function aggregateSources(referrers: AnalyticsBreakdownItem[]): SourceAggregate[] {
	const order: SourceKind[] = ["direct", "search", "social", "other"];
	const total = referrers.reduce((s, r) => s + r.pageViews, 0);
	const groups = new Map<SourceKind, SourceAggregate>(
		order.map((k) => [k, { kind: k, label: SOURCE_META[k].label, color: SOURCE_META[k].color, pageViews: 0, visits: 0, share: 0, hosts: [] }]),
	);
	for (const r of referrers) {
		const g = groups.get(classifyReferrer(r.label))!;
		g.pageViews += r.pageViews;
		g.visits += r.visits;
		if (r.label) g.hosts.push(r);
	}
	for (const g of groups.values()) {
		g.share = total > 0 ? g.pageViews / total : 0;
		g.hosts.sort((a, b) => b.pageViews - a.pageViews);
	}
	return order.map((k) => groups.get(k)!);
}
